import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapPin, Camera, Clock, CheckCircle2, LogIn, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Reads a browser File (from the camera-capture input below) into a base64
// data URL the backend can decode and store.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// A phone camera photo is 2–4 MB (~5 MB once base64-encoded). Uploading that
// over a weak signal inside a hospital outran the request timeout and dropped
// check-ins/outs mid-upload. Downscaled to 800px at JPEG 0.75 it is ~100 KB —
// still plenty for an evidence photo and for face matching. Any decode failure
// (e.g. a format this browser can't draw) falls back to the original file.
const MAX_PHOTO_PX = 800;
async function compressPhoto(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const scale = Math.min(1, MAX_PHOTO_PX / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
    if (dataUrl.length > 1000) return dataUrl;
  } catch {
    // fall through to the untouched file
  } finally {
    URL.revokeObjectURL(url);
  }
  return fileToBase64(file);
}

// Photo uploads get longer than the app-wide 15s: on a slow mobile uplink a
// legitimate upload can take that long, and cutting it off is what produced
// "timeout of 15000ms exceeded" and the server-side "error parsing the body".
const PHOTO_UPLOAD_TIMEOUT_MS = 60000;
const postPhoto = (url, body) => api.post(url, body, { timeout: PHOTO_UPLOAD_TIMEOUT_MS });

// One human message per failure — a raw "timeout of 15000ms exceeded" or
// "Network Error" tells an employee nothing about what to do next.
function attendanceErrorMessage(e) {
  if (e?.response) return apiError(e.response.data?.detail);
  if (e?.isAxiosError) {
    if (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT") {
      return "Your connection is too slow right now, so the upload didn't finish. Move to a spot with better signal (or switch between Wi-Fi and mobile data) and try again.";
    }
    return "Couldn't reach the server. Check your internet connection and try again.";
  }
  return e?.message || "Something went wrong — please try again.";
}

// Wraps the browser Geolocation API in a promise with a clear, human error
// message — this is the #1 thing that will go wrong on a real phone (denied
// permission, GPS off, no signal indoors), so it needs to fail loudly.
function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This device/browser doesn't support location — attendance can't be verified without it."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      // accuracy_m is passed through to the backend purely so a refused
      // check-in/check-out can be diagnosed from the server log: a phone that
      // fell back to wifi/cell positioning reports hundreds or thousands of
      // metres, which is wider than any geofence, and that reads as "not
      // within range" even while the employee is standing in the building.
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
      }),
      (err) => reject(new Error(
        err.code === err.PERMISSION_DENIED
          ? "Location permission was denied. Please allow location access and try again."
          : "Couldn't get your location. Make sure GPS is on and try again."
      )),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

// A single "capture" control: opens the phone's front camera directly (via
// the capture="user" attribute — no getUserMedia/canvas plumbing needed) and
// hands the picked file back to the caller.
function CaptureButton({ label, icon: Icon = Camera, onCapture, disabled, testId }) {
  const inputRef = useRef(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        data-testid={testId}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onCapture(file);
        }}
      />
      <Button className="bg-navy hover:bg-navy-700" disabled={disabled} onClick={() => inputRef.current?.click()}>
        <Icon className="h-4 w-4 mr-2" /> {label}
      </Button>
    </>
  );
}

function FaceStatusNote({ status }) {
  const MAP = {
    matched: { text: "Face verified", cls: "text-emerald-700" },
    not_matched: { text: "Face did not match — flagged for HR review", cls: "text-amber-700" },
    disabled: { text: "Recorded with photo (face matching not yet enabled)", cls: "text-slate-500" },
    no_reference: { text: "Recorded with photo (no enrolled face on file)", cls: "text-slate-500" },
    error: { text: "Recorded with photo (face check unavailable this time)", cls: "text-slate-500" },
  };
  const m = MAP[status] || MAP.disabled;
  return <p className={`text-xs mt-1 flex items-center gap-1 ${m.cls}`}><ShieldCheck className="h-3 w-3" /> {m.text}</p>;
}

// Shared "mark today's attendance" widget — the enroll / check-in / check-out
// flow, with no page-level chrome of its own so it can be dropped straight
// onto the Employee Dashboard (the first page an employee lands on) as well
// as the dedicated My Attendance page, which adds the history table below it.
export default function AttendanceWidget() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const { data: status, isLoading, isError, refetch } = useQuery({
    queryKey: ["attendanceStatus"], queryFn: async () => (await api.get("/my/attendance/status")).data,
  });

  const enroll = useMutation({
    mutationFn: async ({ image_base64, latitude, longitude, accuracy_m }) =>
      (await postPhoto("/my/attendance/enroll-face", { image_base64, latitude, longitude, accuracy_m })).data,
    onSuccess: (d) => { toast.success(`Face enrolled at ${d.location_name}`); qc.invalidateQueries({ queryKey: ["attendanceStatus"] }); },
  });
  const checkIn = useMutation({
    mutationFn: async ({ image_base64, latitude, longitude, accuracy_m }) =>
      (await postPhoto("/my/attendance/check-in", { image_base64, latitude, longitude, accuracy_m })).data,
    onSuccess: (d) => {
      toast.success(`Checked in at ${d.location_name}`);
      if (d.attendance_note) {
        toast(d.attendance_note, { duration: 8000, icon: d.status === "Half Day" ? "⚠️" : "ℹ️" });
      }
      qc.invalidateQueries({ queryKey: ["attendanceStatus"] });
      qc.invalidateQueries({ queryKey: ["attendanceHistory"] });
    },
  });
  const checkOut = useMutation({
    mutationFn: async ({ image_base64, latitude, longitude, accuracy_m }) =>
      (await postPhoto("/my/attendance/check-out", { image_base64, latitude, longitude, accuracy_m })).data,
    onSuccess: (d) => {
      toast.success(`Checked out — ${d.hours_worked}h logged today`);
      if (d.attendance_note) {
        toast(d.attendance_note, { duration: 8000, icon: d.status === "Half Day" ? "⚠️" : "ℹ️" });
      }
      if (d.comp_off_credit) {
        toast.success(d.comp_off_credit.status === "Approved"
          ? "1 comp-off day added to your balance"
          : "1 comp-off day credited — pending your manager's approval", { duration: 8000 });
      }
      qc.invalidateQueries({ queryKey: ["attendanceStatus"] });
      qc.invalidateQueries({ queryKey: ["attendanceHistory"] });
    },
  });

  // Shared capture-then-submit flow for enroll / check-in / check-out.
  const runCapture = async (file, mutation) => {
    setBusy(true);
    try {
      const [image_base64, { latitude, longitude, accuracy_m }] = await Promise.all([compressPhoto(file), getPosition()]);
      await mutation.mutateAsync({ image_base64, latitude, longitude, accuracy_m });
    } catch (e) {
      toast.error(attendanceErrorMessage(e));
      // A dropped connection can lose the server's reply after the check-in
      // was already recorded. Re-read today's status so the widget shows what
      // really happened instead of inviting a retry that gets "already checked in".
      qc.invalidateQueries({ queryKey: ["attendanceStatus"] });
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const fieldStaff = !!status.field_staff;
  const noLocation = !fieldStaff && (status.assigned_locations || []).length === 0;
  const today = status.today;

  return (
    <div data-testid="attendance-widget">
      {fieldStaff && (
        <Card className="p-5 border-teal/30 bg-teal/5 mb-4">
          <p className="text-sm text-navy">You're marked as <strong>Field / Remote Staff</strong> — no fixed location, shift or timing is required. Just capture a live photo each time you check in and out.</p>
        </Card>
      )}

      {noLocation && (
        <Card className="p-5 border-amber-200 bg-amber-50 mb-4">
          <p className="text-sm text-amber-800">No work location has been assigned to you yet. Ask HR to set it before you can enroll or check in.</p>
        </Card>
      )}

      {!status.enrolled && !noLocation && (
        <Card className="p-6 border-slate-200 shadow-none mb-4">
          <h3 className="font-serif-display text-xl text-navy mb-1">One-time face enrollment</h3>
          <p className="text-sm text-slate-500 mb-4">
            {fieldStaff
              ? "Required on your first day. Take a clear, front-facing photo with your camera — you can do this from anywhere."
              : "Required on your first day. You must be standing at your assigned location — take a clear, front-facing photo with your camera."}
          </p>
          <CaptureButton label={busy ? "Enrolling…" : "Capture & Enroll"} icon={Camera} disabled={busy}
            testId="enroll-face-capture" onCapture={(file) => runCapture(file, enroll)} />
        </Card>
      )}

      {status.enrolled && !today && !noLocation && (
        <Card className="p-6 border-slate-200 shadow-none mb-4">
          <h3 className="font-serif-display text-xl text-navy mb-1 flex items-center gap-2"><MapPin className="h-5 w-5 text-teal" /> Not checked in yet today</h3>
          <p className="text-sm text-slate-500 mb-4">
            {fieldStaff
              ? "No fixed location required — check in from wherever you are."
              : `Assigned location${status.assigned_locations.length > 1 ? "s" : ""}: ${status.assigned_locations.map((l) => l.name).join(", ")}`}
          </p>
          <CaptureButton label={busy ? "Checking in…" : "Check In"} icon={LogIn} disabled={busy}
            testId="check-in-capture" onCapture={(file) => runCapture(file, checkIn)} />
        </Card>
      )}

      {today && !today.check_out_time && (
        <Card className="p-6 border-slate-200 shadow-none mb-4">
          <h3 className="font-serif-display text-xl text-navy mb-1 flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Checked in</h3>
          <p className="text-sm text-slate-600">Since {new Date(today.check_in_time).toLocaleTimeString()}</p>
          {today.required_check_out && (
            <p className="text-xs text-slate-500 mt-1">Complete your shift by {new Date(today.required_check_out).toLocaleTimeString()} today.</p>
          )}
          {today.late_minutes > 0 && (
            <p className={`text-xs mt-1 ${today.late_outcome === "half_day" ? "text-red-600" : "text-amber-700"}`}>
              {today.late_minutes} minutes late{today.late_outcome === "half_day" ? " — marked Half Day" : " — within grace"}.
            </p>
          )}
          {today.emergency_cover && (
            <p className="text-xs mt-1 text-amber-700">Checked in at a different location than usual — flagged for your manager to reconcile.</p>
          )}
          <FaceStatusNote status={today.face_status} />
          <div className="mt-4">
            <CaptureButton label={busy ? "Checking out…" : "Check Out"} icon={LogOut} disabled={busy}
              testId="check-out-capture" onCapture={(file) => runCapture(file, checkOut)} />
          </div>
        </Card>
      )}

      {today && today.check_out_time && (
        <Card className={`p-6 border-slate-200 shadow-none mb-4 ${today.status === "Half Day" ? "bg-amber-50/40" : "bg-emerald-50/40"}`}>
          <h3 className="font-serif-display text-xl text-navy mb-1 flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Day complete — {today.status || "Present"}</h3>
          <p className="text-sm text-slate-600">
            {new Date(today.check_in_time).toLocaleTimeString()} – {new Date(today.check_out_time).toLocaleTimeString()} · {today.hours_worked}h logged
          </p>
          {today.early_minutes > 0 && (
            <p className={`text-xs mt-1 ${today.early_outcome === "half_day" ? "text-red-600" : "text-amber-700"}`}>
              Checked out {today.early_minutes} minutes early{today.early_outcome === "half_day" ? " — marked Half Day" : " — within grace"}.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
